import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import { Vector3, BufferGeometry, BufferAttribute } from 'three'
import * as THREE from 'three'

/**
 * Scene3D - Cinematic 3D Background Component
 * 
 * Creates a luxury 3D background with:
 * - Animated gradient mesh with vertex displacement
 * - Floating particle field (1000 instances)
 * - Custom shader materials with fresnel glow
 * - Subtle camera rotation for cinematic feel
 * 
 * Performance Optimizations:
 * - Instanced rendering for particles
 * - Optimized shader uniforms
 * - Frame rate capping for mobile devices
 * 
 * Animation Timeline:
 * - Gradient color cycle: 8 seconds
 * - Particle drift: 6 seconds
 * - Vertex displacement: 4 seconds
 */

// Create Flying Birds Geometry
function createBirdsGeometry() {
  const geometry = new BufferGeometry()
  const vertices = []
  
  // Create flock of birds
  const numBirds = 8
  const flockRadius = 2
  
  for (let i = 0; i < numBirds; i++) {
    const angle = (i / numBirds) * Math.PI * 2
    const radius = flockRadius + Math.random() * 1
    const height = (Math.random() - 0.5) * 3
    
    // Bird position
    const birdX = Math.cos(angle) * radius
    const birdY = height
    const birdZ = Math.sin(angle) * radius
    
    // Create bird shape (simple V-shape)
    const wingLength = 0.4
    const wingAngle = Math.random() * 0.5 + 0.2
    
    // Bird body (center line)
    vertices.push(birdX, birdY, birdZ)
    vertices.push(birdX + Math.cos(angle + wingAngle) * wingLength, birdY, birdZ + Math.sin(angle + wingAngle) * wingLength)
    
    // Left wing
    vertices.push(birdX, birdY, birdZ)
    vertices.push(birdX + Math.cos(angle - wingAngle) * wingLength, birdY, birdZ + Math.sin(angle - wingAngle) * wingLength)
    
    // Right wing
    vertices.push(birdX, birdY, birdZ)
    vertices.push(birdX + Math.cos(angle + wingAngle) * wingLength, birdY, birdZ + Math.sin(angle + wingAngle) * wingLength)
  }
  
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(vertices), 3))
  return geometry
}

// Simple gradient material for background
// const gradientMaterial = new THREE.MeshBasicMaterial({
//   color: '#1A1A1A',
//   transparent: true,
//   opacity: 0.3
// })

// Particle component for floating particles
function Particle({ position, index }: { position: Vector3; index: number }) {
  const meshRef = useRef<THREE.Mesh>(null)
  
  useFrame((state) => {
    if (meshRef.current) {
      // Floating animation with individual timing
      meshRef.current.position.y += Math.sin(state.clock.elapsedTime + index) * 0.001
      meshRef.current.position.x += Math.cos(state.clock.elapsedTime * 0.5 + index) * 0.0005
      meshRef.current.rotation.y += 0.01
    }
  })
  
  return (
    <mesh ref={meshRef} position={position}>
      <sphereGeometry args={[0.02, 8, 8]} />
      <meshBasicMaterial 
        color="#D4AF37" 
        transparent 
        opacity={0.6}
      />
    </mesh>
  )
}

// Luxury Crystal Structure Component
// function LuxuryCrystal({ position, rotation, scale, color }: { 
//   position: [number, number, number], 
//   rotation: [number, number, number], 
//   scale: number,
//   color: string 
// }) {
//   const meshRef = useRef<THREE.Mesh>(null)
//   
//   useFrame((state) => {
//     if (meshRef.current) {
//       meshRef.current.rotation.y += 0.002
//       meshRef.current.rotation.x += 0.001
//       meshRef.current.position.y += Math.sin(state.clock.elapsedTime + position[0]) * 0.001
//     }
//   })
//   
//   return (
//     <mesh ref={meshRef} position={position} rotation={rotation} scale={scale}>
//       <octahedronGeometry args={[1, 0]} />
//       <meshBasicMaterial 
//         color={color}
//         transparent
//         opacity={0.7}
//       />
//     </mesh>
//   )
// }

// Luxury Torus Knot - Amazing Complex Shape
// function LuxuryKnot({ position, rotation, scale }: { 
//   position: [number, number, number], 
//   rotation: [number, number, number], 
//   scale: number 
// }) {
//   const meshRef = useRef<THREE.Mesh>(null)
//   
//   useFrame((state) => {
//     if (meshRef.current) {
//       meshRef.current.rotation.y += 0.002
//       meshRef.current.rotation.x += 0.001
//       meshRef.current.rotation.z += 0.0005
//     }
//   })
//   
//   return (
//     <mesh ref={meshRef} position={position} rotation={rotation} scale={scale}>
//       <torusKnotGeometry args={[1, 0.3, 100, 16]} />
//       <meshBasicMaterial 
//         color="#D4AF37"
//         transparent
//         opacity={0.7}
//       />
//     </mesh>
//   )
// }

// Luxury Dodecahedron - 12-sided Crystal
// function LuxuryDodecahedron({ position, rotation, scale, color }: { 
//   position: [number, number, number], 
//   rotation: [number, number, number], 
//   scale: number,
//   color: string 
// }) {
//   const meshRef = useRef<THREE.Mesh>(null)
//   
//   useFrame((state) => {
//     if (meshRef.current) {
//       meshRef.current.rotation.y += 0.001
//       meshRef.current.rotation.x += 0.0005
//       meshRef.current.position.y += Math.sin(state.clock.elapsedTime + position[0]) * 0.002
//     }
//   })
//   
//   return (
//     <mesh ref={meshRef} position={position} rotation={rotation} scale={scale}>
//       <dodecahedronGeometry args={[1, 0]} />
//       <meshBasicMaterial 
//         color={color}
//         transparent
//         opacity={0.8}
//       />
//     </mesh>
//   )
// }

// Flying Birds - Elegant and Free
function FlyingBirds() {
  const leftFlockRef = useRef<THREE.LineSegments>(null)
  const rightFlockRef = useRef<THREE.LineSegments>(null)
  
  const birdsGeometry = useMemo(() => createBirdsGeometry(), [])
  
  useFrame((state) => {
    // Flying animation - birds soaring through the sky
    if (leftFlockRef.current) {
      leftFlockRef.current.rotation.y += 0.001
      leftFlockRef.current.position.y += Math.sin(state.clock.elapsedTime * 0.5) * 0.002
      leftFlockRef.current.position.x += Math.sin(state.clock.elapsedTime * 0.3) * 0.001
    }
    
    if (rightFlockRef.current) {
      rightFlockRef.current.rotation.y += 0.001
      rightFlockRef.current.position.y += Math.sin(state.clock.elapsedTime * 0.5 + 1) * 0.002
      rightFlockRef.current.position.x += Math.sin(state.clock.elapsedTime * 0.3 + 1) * 0.001
    }
  })
  
  return (
    <group>
      {/* Left Flock of Birds - Far Left Side */}
      <lineSegments ref={leftFlockRef} geometry={birdsGeometry} position={[-5, 0, -1]} scale={0.8}>
        <lineBasicMaterial 
          color="#FF6B6B" 
          transparent 
          opacity={0.8}
        />
      </lineSegments>
      
      {/* Right Flock of Birds - Far Right Side */}
      <lineSegments ref={rightFlockRef} geometry={birdsGeometry} position={[5, 0, -1]} scale={0.8}>
        <lineBasicMaterial 
          color="#4ECDC4" 
          transparent 
          opacity={0.8}
        />
      </lineSegments>
    </group>
  )
}

// Luxury Torus Ring Component
// function LuxuryRing({ position, rotation, scale }: { 
//   position: [number, number, number], 
//   rotation: [number, number, number], 
//   scale: number 
// }) {
//   const meshRef = useRef<THREE.Mesh>(null)
//   
//   useFrame((state) => {
//     if (meshRef.current) {
//       meshRef.current.rotation.y += 0.003
//       meshRef.current.rotation.z += 0.001
//     }
//   })
//   
//   return (
//     <mesh ref={meshRef} position={position} rotation={rotation} scale={scale}>
//       <torusGeometry args={[2, 0.3, 16, 100]} />
//       <meshBasicMaterial 
//         color="#D4AF37"
//         transparent
//         opacity={0.6}
//       />
//     </mesh>
//   )
// }

// Main 3D Scene Component
export default function Scene3D() {
  // Generate subtle particle positions
  const particles = useMemo(() => {
    const positions: Vector3[] = []
    for (let i = 0; i < 800; i++) {
      positions.push(new Vector3(
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 20
      ))
    }
    return positions
  }, [])
  
  return (
    <>
      {/* Subtle lighting setup */}
      <ambientLight intensity={0.3} />
      
      {/* Main directional light */}
      <directionalLight 
        position={[5, 5, 5]} 
        intensity={0.5}
        castShadow={false}
        color="#D4AF37"
      />
      
      {/* Secondary accent light */}
      <directionalLight 
        position={[-5, 3, -5]} 
        intensity={0.2}
        castShadow={false}
        color="#8B5CF6"
      />
      
      {/* Point light for subtle luxury */}
      <pointLight 
        position={[0, 0, 5]} 
        intensity={0.3}
        color="#3B82F6"
        distance={20}
      />
      
      {/* FLYING BIRDS - Elegant and Free */}
      <FlyingBirds />
      
      
      {/* Enhanced floating particle field */}
      {particles.map((position, index) => (
        <Particle 
          key={index} 
          position={position} 
          index={index}
        />
      ))}
    </>
  )
}
